<template>
  <div
    class="card"
    :class="framed ? ['trs-card', 'trs-card--' + tone] : null"
  >
    <div class="card-header py-2 d-flex align-items-center justify-content-between">
      <h6 class="mb-0">
        <feather-icon
          icon="ClipboardIcon"
          size="15"
          class="mr-50 text-primary"
        />
        {{ $t('despacho.requestSummary.title') }}
        <b-spinner
          v-if="loading"
          small
          class="ml-50"
        />
      </h6>
      <div
        v-if="requests.length"
        class="trs-counters"
      >
        <span
          v-if="counts.pending"
          class="trs-badge trs-badge--pending"
        >{{ $t('despacho.requestSummary.pendingCount', { count: counts.pending }) }}</span>
        <span
          v-if="counts.fulfilled"
          class="trs-badge trs-badge--fulfilled"
        >{{ $t('despacho.requestSummary.fulfilledCount', { count: counts.fulfilled }) }}</span>
        <span
          v-if="counts.cancelled"
          class="trs-badge trs-badge--cancelled"
        >{{ $t('despacho.requestSummary.cancelledCount', { count: counts.cancelled }) }}</span>
      </div>
    </div>

    <div class="card-body p-0">
      <div
        v-if="!loading && !requests.length"
        class="trs-empty"
      >
        <feather-icon
          icon="InfoIcon"
          size="14"
          class="mr-50"
        />
        {{ $t('despacho.requestSummary.empty') }}
      </div>

      <div
        v-for="req in requests"
        :key="req.id"
        class="trs-row"
        :class="'trs-row--' + statusKey(req.status)"
      >
        <div class="trs-main">
          <div class="trs-title">
            <span class="trs-dot" />
            {{ req.team_type ? req.team_type.name : $t('despacho.requestSummary.removedType') }}
            <span
              v-if="req.dispatch_code"
              class="trs-code"
            >{{ req.dispatch_code.code }}</span>
          </div>

          <div
            v-if="req.technical_decision"
            class="trs-line"
          >
            <span class="trs-label">{{ $t('despacho.requestSummary.decision') }}</span>
            {{ req.technical_decision.name }}
          </div>

          <div
            v-if="req.notes"
            class="trs-line"
          >
            <span class="trs-label">{{ $t('despacho.requestSummary.notes') }}</span>
            {{ req.notes }}
          </div>

          <div
            v-if="req.status === 3 && req.cancel_notes"
            class="trs-line"
          >
            <span class="trs-label">{{ $t('despacho.requestSummary.cancelReason') }}</span>
            {{ req.cancel_notes }}
          </div>

          <div class="trs-meta">
            <feather-icon
              icon="UserIcon"
              size="11"
              class="mr-25"
            />
            {{ $t('despacho.requestSummary.requestedBy', { name: req.requested_by ? req.requested_by.name : '—' }) }}
            · {{ formatDateTime(req.created_at) }}
            <template v-if="req.status === 3 && req.cancelled_by">
              · {{ $t('despacho.requestSummary.cancelledBy', { name: req.cancelled_by.name }) }}
            </template>
          </div>
        </div>

        <!-- Progresso: quantas unidades do pedido já foram despachadas. -->
        <div class="trs-progress">
          <div class="trs-progress__text">
            {{ $t('despacho.requestSummary.dispatchedOf', { done: dispatchedUnits(req), total: req.quantity }) }}
          </div>
          <div class="trs-progress__bar">
            <div
              class="trs-progress__fill"
              :style="{ width: progressPercent(req) + '%' }"
            />
          </div>
        </div>

        <div class="trs-status">
          <span
            class="trs-badge"
            :class="'trs-badge--' + statusKey(req.status)"
          >{{ statusLabel(req.status) }}</span>
        </div>
      </div>

      <suggested-team-types :types="suggestedTypes" />
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import SuggestedTeamTypes from './SuggestedTeamTypes.vue'

const STATUS_KEYS = { 1: 'pending', 2: 'fulfilled', 3: 'cancelled' }

/**
 * Resumo do pedido de equipe feito no atendimento (regulação), exibido no
 * detalhe do Despacho: o que foi pedido, por quem, quanto já foi despachado
 * e o desfecho. Recebe a mesma lista do TeamRequestsMixin, que já se
 * atualiza em tempo real.
 */
export default {
  name: 'OccurrenceTeamRequestSummary',
  components: { BSpinner, SuggestedTeamTypes },

  props: {
    requests: { type: Array, default: () => [] },
    suggestedTypes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    // Cartão com moldura e faixa de cor (aba só leitura do Atendimento), no mesmo estilo do cartão da equipe.
    framed: { type: Boolean, default: false },
  },

  computed: {
    // Cor da faixa: pendente se ainda falta despachar algo; senão atendido; senão só cancelados.
    tone() {
      if (this.counts.pending) return 'pending'

      return this.counts.fulfilled ? 'fulfilled' : 'cancelled'
    },
    counts() {
      return this.requests.reduce((acc, r) => {
        acc[this.statusKey(r.status)] += 1
        return acc
      }, { pending: 0, fulfilled: 0, cancelled: 0 })
    },
  },

  methods: {
    statusKey(status) {
      return STATUS_KEYS[status] || 'pending'
    },

    statusLabel(status) {
      return this.$t(`despacho.requestSummary.status.${this.statusKey(status)}`)
    },

    dispatchedUnits(req) {
      const remaining = Number.isFinite(req.remaining) ? req.remaining : req.quantity
      return Math.max(0, req.quantity - remaining)
    },

    progressPercent(req) {
      if (!req.quantity) return 0
      return Math.min(100, Math.round((this.dispatchedUnits(req) / req.quantity) * 100))
    },

    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.trs-counters { display: flex; gap: 6px; flex-wrap: wrap; }

.trs-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.trs-badge--pending   { background: #fff3e8; color: #b45309; }
.trs-badge--fulfilled { background: #e8f7ee; color: #15803d; }
.trs-badge--cancelled { background: #f1f2f4; color: #6b7280; }

.trs-empty {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  font-size: 13px;
  color: #6e6b7b;
}

.trs-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px 110px;
  gap: 16px;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid #f2f0f7;
}
.trs-row:first-of-type { border-top: 0; }

.trs-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #5e5873;
}
.trs-row--cancelled .trs-title { text-decoration: line-through; color: #b9b9c3; }

.trs-dot {
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  flex: 0 0 8px;
}
.trs-row--pending   .trs-dot { background: #ff9f43; }
.trs-row--fulfilled .trs-dot { background: #28c76f; }
.trs-row--cancelled .trs-dot { background: #b9b9c3; }

.trs-code {
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 9px;
  font-size: 10.5px;
  font-weight: 700;
  background: #eceffd;
  color: #4c5eb5;
}

.trs-line { margin-top: 3px; font-size: 12.5px; color: #6e6b7b; }
.trs-label { font-weight: 600; margin-right: 4px; }
.trs-meta { margin-top: 5px; font-size: 11.5px; color: #8a8797; }

.trs-progress__text { font-size: 12px; color: #6e6b7b; margin-bottom: 4px; }
.trs-progress__bar { height: 6px; border-radius: 3px; background: #eeedf3; overflow: hidden; }
.trs-progress__fill { height: 100%; border-radius: 3px; background: #28c76f; transition: width 0.2s; }
.trs-row--pending .trs-progress__fill { background: #ff9f43; }
.trs-row--cancelled .trs-progress__fill { background: #b9b9c3; }

.trs-status { text-align: right; }

/* Abaixo de md: progresso e status descem para baixo do título. */
@media (max-width: 767.98px) {
  .trs-row { grid-template-columns: 1fr auto; }
  .trs-progress { grid-column: 1 / -1; grid-row: 2; }
}

/* Versão com moldura: mesma linguagem do cartão da equipe despachada (faixa lateral, cantos e sombra). */
.trs-card {
  overflow: hidden;
  border: 1px solid #ebe9f1;
  border-left: 5px solid #ff9f43;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(34, 41, 47, 0.06);
}
.trs-card--fulfilled { border-left-color: #28c76f; }
.trs-card--cancelled { border-left-color: #b9b9c3; }

.trs-card > .card-header { padding: 16px 20px 6px !important; border: 0; background: transparent; }
.trs-card > .card-header h6 { font-size: 1rem; font-weight: 700; }
.trs-card .trs-row { padding: 12px 20px 16px; border-top: 0; }
.trs-card .trs-title { font-size: 1.05rem; font-weight: 700; }
.trs-card .trs-line { font-size: 0.86rem; }
.trs-card .trs-progress__bar { height: 8px; border-radius: 4px; }
.trs-card .trs-progress__fill { border-radius: 4px; }

.dark-layout .trs-card { border-color: #3b4253; background: #283046; box-shadow: none; }
.dark-layout .trs-row { border-color: #3b4253; }
.dark-layout .trs-title { color: #d0d2d6; }
.dark-layout .trs-line,
.dark-layout .trs-empty,
.dark-layout .trs-progress__text { color: #b4b7bd; }
.dark-layout .trs-progress__bar { background: #3b4253; }
.dark-layout .trs-code { background: #1f2547; color: #a3b0ff; }
.dark-layout .trs-badge--pending   { background: #3a2a14; color: #ffb976; }
.dark-layout .trs-badge--fulfilled { background: #14301f; color: #4ade80; }
.dark-layout .trs-badge--cancelled { background: #2c3142; color: #a0a3ad; }
</style>
